<template>
    <section class="shipment-page">
        <div class="shipment-page__header">
            <button class="shipment-page__back-btn" @click="goBack">
                <v-icon class="shipment-page__back-icon"
                >mdi-chevron-left</v-icon
                >
                {{ $t('pages.shipments.returnToShipments') }}
            </button>
        </div>


        <div class="shipment-page__body">
            <div v-if="isFetching" class="shipment-page__loading">
                <v-progress-circular
                    indeterminate
                    color="primary"
                ></v-progress-circular>
            </div>

            <div v-else-if="shipment" class="shipment-page__container">
                <ShipmentDetails :shipment="shipment" />
                <ShipmentItemsTable
                    :shipment="shipment"
                    :selected-items="selectedItems"
                    :is-closing="isClosing"
                    :closing-item-id="closingItemId"
                    :is-returning="isReturning"
                    :returning-item-id="returningItemId"
                    @update:selectedItems="selectedItems = $event"
                    @close-item="closeItem"
                    @close-selected="closeSelectedItems"
                    @close-all="closeAllItems"
                    @return-item="returnItem"
                    @return-selected="returnSelectedItems"
                    @return-all="returnAllItems"
                />
            </div>

            <div v-else class="shipment-page__error">
                <p>{{ $t('pages.shipments.notFound') }}</p>
            </div>
        </div>
    </section>
</template>

<script>
import languageMixin from '@/mixins/language.mixin'
import ShipmentDetails from '@/components/shipments/shipment-details.vue'
import ShipmentItemsTable from '@/components/shipments/shipment-items-table.vue'
import { mapActions, mapGetters } from 'vuex'
import { actionTypes, getterTypes } from '@/store/modules/shipments'
import { actionTypes as modalNotificationActionTypes } from '@/store/modules/modal-notifications'
import { actionTypes as confirmerActionTypes } from '@/store/modules/confirmer'
import { actionTypes as modalsActionTypes } from '@/store/modules/modals'
import shipmentsApi from '@/api/shipments'
import popUpTypes from '@/types/pop-up-types'
import {
    canReturnItem,
    filterReturnableItems,
    getItemId,
    createSingleReturnPayload,
    createBulkReturnPayload,
    handleReturnError,
    resetSelection,
} from '@/helpers/shipment-items.utils'

export default {
    name: 'ShipmentCard',
    components: { ShipmentDetails, ShipmentItemsTable },
    mixins: [languageMixin],
    data: () => ({
        crumbs: [{ title: 'Home' }],
        selectedItems: [],
        isClosing: false,
        closingItemId: null,
        returningItemId: null,
    }),
    computed: {
        ...mapGetters('shipments', {
            shipment: getterTypes.getSelectedShipment,
            isFetching: getterTypes.isFetching,
            isReturning: getterTypes.isReturning,
        }),
    },
    mounted() {
        const shipmentId = this.$route.params.id
        if (shipmentId) {
            this.fetchShipmentById(shipmentId)
        }
    },
    methods: {
        ...mapActions('shipments', {
            fetchShipmentById: actionTypes.fetchShipmentById,
            returnShipmentItems: actionTypes.returnShipmentItems,
        }),
        ...mapActions('modalNotifications', {
            showError: modalNotificationActionTypes.error,
            showSuccess: modalNotificationActionTypes.success,
        }),
        ...mapActions('confirmer', {
            confirmHandle: confirmerActionTypes.confirmHandle,
        }),
        ...mapActions('modals', {
            openModal: modalsActionTypes.openPopUp,
        }),
        closeItem(item) {
            const itemId = getItemId(item)
            if (!itemId || item.closed_at) return

            this.openPaymentsModal({
                items: [item],
                shipmentId: this.$route.params.id,
                onConfirm: this.handleCloseWithPayments,
            })
        },
        closeSelectedItems() {
            if (this.selectedItems.length === 0) return

            const itemsToClose = this.selectedItems.filter((item) => !item.closed_at)
            if (itemsToClose.length === 0) return

            this.openPaymentsModal({
                items: itemsToClose,
                shipmentId: this.$route.params.id,
                onConfirm: this.handleCloseWithPayments,
            })
        },
        closeAllItems() {
            if (!this.shipment?.items) return

            const itemsToClose = this.shipment.items.filter((item) => !item.closed_at)
            if (itemsToClose.length === 0) return

            this.openPaymentsModal({
                items: itemsToClose,
                shipmentId: this.$route.params.id,
                onConfirm: this.handleCloseWithPayments,
            })
        },
        openPaymentsModal: function (payload) {
            this.openModal({
                name: popUpTypes.shipmentClosePayments,
                payload: {
                    ...payload,
                    onConfirm: (paymentsData) => {
                        payload.onConfirm(paymentsData)
                    },
                },
            })
        },
        handleCloseWithPayments: async function (paymentsData) {
            this.isClosing = true

            try {
                const shipmentId = this.$route.params.id
                const payload = {
                    items: paymentsData.items,
                }
                await shipmentsApi.closeShipmentItems(shipmentId, payload)
                await this.fetchShipmentById(shipmentId)
                this.selectedItems = []
                this.showSuccess(this.$t('pages.shipments.itemsClosed'))
            } catch (error) {
                console.error('Error closing items with payments:', error)
                this.showError(
                    error?.response?.data?.message || this.$t('pages.shipments.closeError')
                )
            } finally {
                this.isClosing = false
                this.closingItemId = null
            }
        },
        async returnItem(item) {
            const itemId = getItemId(item)
            if (!itemId || !canReturnItem(item)) return

            try {
                await this.confirmHandle(this.$t('pages.shipments.confirmReturnItem'))
                this.returningItemId = itemId

                const payload = createSingleReturnPayload(itemId)
                await this.returnShipmentItems({
                    shipmentId: this.$route.params.id,
                    payload,
                })

                resetSelection(this)
                this.showSuccess(this.$t('pages.shipments.itemReturned'))
            } catch (error) {
                handleReturnError(error, this.showError, this.$t)
            } finally {
                this.returningItemId = null
            }
        },
        async returnSelectedItems() {
            if (this.selectedItems.length === 0) return

            const itemsToReturn = filterReturnableItems(this.selectedItems)
            if (itemsToReturn.length === 0) {
                this.showError(this.$t('pages.shipments.allItemsAlreadyReturned'))
                return
            }

            try {
                await this.confirmHandle(
                    this.$t('pages.shipments.confirmReturnItems', {
                        count: itemsToReturn.length,
                    })
                )

                const payload = createBulkReturnPayload(itemsToReturn)
                await this.returnShipmentItems({
                    shipmentId: this.$route.params.id,
                    payload,
                })

                resetSelection(this)
                this.showSuccess(this.$t('pages.shipments.itemsReturned'))
            } catch (error) {
                handleReturnError(error, this.showError, this.$t)
            } finally {
                this.returningItemId = null
            }
        },
        async returnAllItems() {
            if (!this.shipment?.items) return

            const itemsToReturn = filterReturnableItems(this.shipment.items)
            if (itemsToReturn.length === 0) {
                this.showError(this.$t('pages.shipments.allItemsAlreadyReturned'))
                return
            }

            try {
                await this.confirmHandle(this.$t('pages.shipments.confirmReturnAllItems'))

                const payload = createBulkReturnPayload(itemsToReturn)
                await this.returnShipmentItems({
                    shipmentId: this.$route.params.id,
                    payload,
                })

                resetSelection(this)
                this.showSuccess(this.$t('pages.shipments.allItemsReturned'))
            } catch (error) {
                handleReturnError(error, this.showError, this.$t)
            } finally {
                this.returningItemId = null
            }
        },
        goBack() {
            return window.history.length > 1
                ? this.$router.back()
                : this.$router.push('/shipments')
        },
    },
}
</script>

<style lang="scss" scoped>
.shipment-page {
    display: flex;
    flex-direction: column;

    &__header {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        width: 100%;
    }

    &__back-btn {
        width: fit-content;
    }

    &__back-icon {
        @include for-hebrew {
            transform: scale(-1);
        }
    }

    &__body {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 12px;
        gap: 12px;

        @include tablet-up {
            padding: 16px;
            gap: 16px;
        }
    }

    &__loading {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        min-height: 400px;
        padding: 40px;
    }

    &__container {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    &__error {
        text-align: center;
        padding: 40px;
        color: #999;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 400px;
    }
}

</style>
